import { useLayoutEffect, useRef } from "react";
import type { Section } from "../../types/api";
import type { Overlay } from "./InlineOverlay";
import { InlineOverlayBlock } from "./InlineOverlay";

interface Props {
  html: string;
  sections: Section[];
  collapsed: Record<string, boolean>;
  onSetActive: (id: string) => void;
  onToggleCollapse: (id: string) => void;
  overlays: Record<string, Overlay[]>;
  onOverlayClose: (secId: string, idx: number) => void;
}

/**
 * Renders pre-converted HTML in the prose container. Uses dangerouslySet
 * because the HTML pipeline (mdToHtml) is fully under our control and emits
 * only known tags with escaped content. No untrusted input flows through.
 *
 * Annotations + collapsible sections are handled here via data attributes;
 * sections wrap content from H2 to next H2.
 */
export function ProseBody({
  html,
  sections,
  collapsed,
  onSetActive,
  overlays,
  onOverlayClose,
}: Props) {
  const articleRef = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (!html) return;
    const article = articleRef.current;
    if (!article) return;
    const contentRoot = article.querySelector<HTMLElement>("[data-prose-root]");
    if (!contentRoot) return;

    const headings = Array.from(contentRoot.querySelectorAll<HTMLElement>("h1,h2,h3,h4,h5,h6"));
    for (const heading of headings) {
      heading.removeAttribute("id");
      heading.removeAttribute("data-section-id");
      heading.removeAttribute("data-section-heading");
    }
    for (const node of contentRoot.querySelectorAll<HTMLElement>("[data-section-id]")) {
      node.removeAttribute("data-section-id");
    }
    for (const node of contentRoot.querySelectorAll<HTMLElement>("[data-section-hidden]")) {
      node.removeAttribute("data-section-hidden");
      node.style.display = "";
    }

    const matchedHeadings = mapSectionsToHeadings(sections, headings);
    for (const [sectionId, heading] of matchedHeadings) {
      heading.id = sectionId;
      heading.dataset.sectionId = sectionId;
      heading.dataset.sectionHeading = "true";
    }

    const children = Array.from(contentRoot.children).filter(
      (node): node is HTMLElement => node instanceof HTMLElement,
    );
    for (const section of sections) {
      const heading = matchedHeadings.get(section.id);
      if (!heading) continue;

      heading.dataset.sectionId = section.id;
      let sibling = heading.nextElementSibling;
      while (sibling instanceof HTMLElement) {
        if (isHeadingAtOrAbove(sibling, section.heading_level)) break;
        sibling.dataset.sectionId = section.id;
        sibling = sibling.nextElementSibling;
      }
    }

    for (const section of sections) {
      if (!collapsed[section.id] || section.heading_level !== 2) continue;
      const heading = matchedHeadings.get(section.id);
      if (!heading) continue;
      let sibling = heading.nextElementSibling;
      while (sibling instanceof HTMLElement && !isHeadingAtOrAbove(sibling, 2)) {
        sibling.dataset.sectionHidden = "true";
        sibling.style.display = "none";
        sibling = sibling.nextElementSibling;
      }
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        const id = visible?.target.getAttribute("id");
        if (id) onSetActive(id);
      },
      {
        root: document.getElementById("doc-main-content"),
        rootMargin: "0px 0px -70% 0px",
        threshold: [0, 1],
      },
    );

    for (const child of children) {
      if (child.dataset.sectionHeading === "true") observer.observe(child);
    }
    return () => observer.disconnect();
  });

  return (
    <article ref={articleRef} className="prose" data-prose>
      {/** biome-ignore lint/security/noDangerouslySetInnerHtml: HTML is generated by our trusted pipeline */}
      <div data-prose-root dangerouslySetInnerHTML={{ __html: html }} />
      {Object.entries(overlays).flatMap(([secId, list]) =>
        list.map((ov, i) => (
          <InlineOverlayBlock
            key={`${secId}-${ov.title}-${i}`}
            overlay={ov}
            onClose={() => onOverlayClose(secId, i)}
          />
        )),
      )}
    </article>
  );
}

function normalizeHeading(text: string): string {
  return text.toLowerCase().replace(/\s+/g, " ").trim();
}

function headingLevel(el: Element): number | null {
  const match = /^H([1-6])$/.exec(el.tagName);
  return match?.[1] ? Number(match[1]) : null;
}

function isHeadingAtOrAbove(el: Element, level: number): boolean {
  const current = headingLevel(el);
  return current != null && current <= level;
}

function mapSectionsToHeadings(
  sections: Section[],
  headings: HTMLElement[],
): Map<string, HTMLElement> {
  const matches = new Map<string, HTMLElement>();
  let cursor = 0;

  for (const section of sections) {
    const label = normalizeHeading(section.heading_path[section.heading_path.length - 1] ?? "");
    const tagName = `H${section.heading_level}`;
    let matchIndex = -1;

    for (let i = cursor; i < headings.length; i += 1) {
      const heading = headings[i];
      if (!heading) continue;
      if (heading.tagName !== tagName) continue;
      if (normalizeHeading(heading.textContent ?? "") === label) {
        matchIndex = i;
        break;
      }
    }

    if (matchIndex < 0) {
      for (let i = cursor; i < headings.length; i += 1) {
        const heading = headings[i];
        if (heading?.tagName === tagName) {
          matchIndex = i;
          break;
        }
      }
    }

    const heading = headings[matchIndex];
    if (heading) {
      matches.set(section.id, heading);
      cursor = matchIndex + 1;
    }
  }

  return matches;
}
